import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Icon, IconComponent } from '@examdojo/core/icon';
import {
  PAYMENT_STATUS_BADGE_COLOR_CLASSES,
  PAYOUT_REQUEST_STATUS_LABELS,
  PayoutRequestStatus,
} from '../payout-request/payout-request.model';

export interface StatusBadgeConfig {
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

@Component({
  selector: 'dojo-payment-status-badge',
  template: `
    <span [class]="badgeClasses()">
      @if (config().showIcon) {
        <dojo-icon [icon]="getIconName()" [class]="iconClasses()" />
      }
      {{ statusLabel() }}
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IconComponent],
})
export class PaymentStatusBadgeComponent {
  readonly status = input.required<PayoutRequestStatus | 'ALL'>();
  readonly config = input<StatusBadgeConfig>({});

  readonly statusLabel = computed(() => {
    const status = this.status();
    if (status === 'ALL') {
      return 'All Statuses';
    }
    return PAYOUT_REQUEST_STATUS_LABELS[status];
  });

  readonly badgeClasses = computed(() => {
    const status = this.status();
    const config = this.config();
    const size = config.size || 'md';
    const showIcon = config.showIcon || false;

    const baseClasses = 'inline-flex items-center justify-start rounded-[6px] font-semibold';
    const sizeClasses = this.getSizeClasses(size);
    const colorClasses = this.getColorClasses(status);
    const gapClass = showIcon ? 'gap-1' : '';
    // Fixed height and minimum width for consistent alignment
    const heightClass = size === 'sm' ? 'h-6 min-w-fit' : size === 'lg' ? 'h-8 min-w-fit' : 'h-7 min-w-fit';

    return `${baseClasses} ${sizeClasses} ${colorClasses} ${gapClass} ${heightClass}`.trim();
  });

  readonly iconClasses = computed(() => {
    const config = this.config();
    const size = config.size || 'md';
    return size === 'sm'
      ? 'h-3.5 w-3.5 flex-shrink-0'
      : size === 'lg'
        ? 'h-5 w-5 flex-shrink-0'
        : 'h-4 w-4 flex-shrink-0';
  });

  getIconName(): Icon {
    const status = this.status();
    switch (status) {
      case 'DRAFT':
        return 'file-edit';
      case 'WAITING_FOR_APPROVAL':
        return 'clock';
      case 'APPROVED':
        return 'check-circle';
      case 'REJECTED':
        return 'times-circle';
      case 'PAYMENT_SUCCEEDED':
        return 'dollar-sign';
      case 'PAYMENT_FAILED':
        return 'exclamation-triangle';
      case 'ALL':
        return 'list';
      default:
        return 'file';
    }
  }

  private getSizeClasses(size: 'sm' | 'md' | 'lg'): string {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-xs';
      case 'lg':
        return 'px-3 py-1.5 text-sm';
      default:
        return 'px-2.5 py-1.5 text-xs';
    }
  }

  private getColorClasses(status: PayoutRequestStatus | 'ALL'): string {
    if (status === 'ALL') {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
    return PAYMENT_STATUS_BADGE_COLOR_CLASSES[status];
  }
}
